import { url } from '@vuelidate/validators';
import {
  processVariable,
  buildWhatsAppProcessedParams,
  COMPONENT_TYPES as UPSTREAM_COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from '@chatwoot/utils';

// Constants and pure template helpers are shared with the mobile app via
// @chatwoot/utils so the logic lives in one place.
export {
  MEDIA_FORMATS,
  findComponentByType,
  processVariable,
  renderTemplatePreview,
} from '@chatwoot/utils';

export const COMPONENT_TYPES = {
  ...UPSTREAM_COMPONENT_TYPES,
  FOOTER: 'FOOTER',
};

export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_CATEGORY = 'UTILITY';

export const allKeysRequired = value => {
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};

export const replaceTemplateVariables = (templateText, processedParams) => {
  return templateText.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.body?.[variableKey] || `{{${variable}}}`;
  });
};

export const hasTemplateVariables = template => {
  if (!template?.components) return false;

  const body = findComponentByType(template, COMPONENT_TYPES.BODY);
  if (body?.text?.match(/\{\{([^}]+)\}\}/g)) return true;

  const buttonsComponent = findComponentByType(
    template,
    COMPONENT_TYPES.BUTTONS
  );
  if (buttonsComponent?.buttons) {
    const hasButtonVars = buttonsComponent.buttons.some(
      button =>
        (button.type === 'URL' && button.url?.includes('{{')) ||
        button.type === 'COPY_CODE'
    );
    if (hasButtonVars) return true;
  }

  return false;
};

export const hasTemplateMediaHeader = template => {
  if (!template?.components) return false;

  const header = findComponentByType(template, COMPONENT_TYPES.HEADER);
  return header ? MEDIA_FORMATS.includes(header.format) : false;
};

// The media-header flag is derived from the template inside the shared helper;
// the second argument is kept for backwards-compatible call sites.
export const buildTemplateParameters = template => {
  const params = buildWhatsAppProcessedParams(template);
  // The fork sends a compact button array; the backend restores template indices.
  if (params.buttons) params.buttons = params.buttons.filter(Boolean);
  return params;
};

export const templateButtonError = (button, allowLiquid = false) => {
  const value = button.parameter?.trim() || '';
  if (!value) return 'WHATSAPP_TEMPLATES.PARSER.VARIABLE_REQUIRED';
  // Campaign values are validated again on the server after Liquid resolution.
  if (allowLiquid && /{{[\s\S]*?}}|{%[\s\S]*?%}/.test(value)) return '';
  if (button.type === 'copy_code' && [...value].length > 15) {
    return 'WHATSAPP_TEMPLATES.PARSER.COUPON_CODE_TOO_LONG';
  }
  return '';
};

// 360dialog templates may not have a provider ID.
export const getTemplateKey = template =>
  template.id ?? `${template.name}:${template.language}`;

export const isValidTemplateMediaUrl = value =>
  !value || (/^https?:\/\//i.test(value) && url.$validator(value));

// Match the legacy body-only formats still supported by TemplateParameterConverterService.
export const normalizeTemplateParameters = (params = {}) => {
  const componentKeys = ['body', 'header', 'footer', 'buttons'];
  const isParameterMap = value =>
    value == null || (typeof value === 'object' && !Array.isArray(value));
  const validButtons =
    params.buttons == null ||
    (Array.isArray(params.buttons) &&
      params.buttons.every(
        button => button == null || (isParameterMap(button) && button.type)
      ));
  if (
    componentKeys.some(key => key in params) &&
    isParameterMap(params.body) &&
    isParameterMap(params.header) &&
    validButtons
  ) {
    return params;
  }
  const entries = Array.isArray(params)
    ? params.map((value, index) => [String(index + 1), String(value ?? '')])
    : Object.entries(params).map(([key, value]) => [key, String(value ?? '')]);
  return entries.length ? { body: Object.fromEntries(entries) } : {};
};
